import {
  CYCLE_STATUS_LABELS,
  MEMBERSHIP_STATUS_LABELS,
  USER_TYPE_TO_PROFILE,
} from './statuses';

const EM_DASH = '—';
const EN_DASH = '–';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const ordinalSuffix = (day) => {
  // 11th, 12th, 13th are the exceptions to the 1st/2nd/3rd rule.
  if (day >= 11 && day <= 13) return 'th';

  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

/**
 * Two kinds of date arrive from this service, and they are formatted
 * differently on purpose.
 *
 * The application and cycle dates are **calendar dates** (`YYYY-MM-DD`).
 * "August 15th" means that day wherever you read it, so they are formatted by
 * string split and never parsed: `new Date('2026-08-15')` is UTC midnight, and
 * renders as the 14th anywhere behind UTC — including America/New_York, the
 * service's business timezone.
 *
 * `createdAt` is an **instant**, and an admin wants it in their own timezone,
 * so `formatCreatedAtShort` does build a Date. See its note below.
 */

/** `YYYY-MM-DD` as "August 15th, 2026". */
export const formatCycleDate = (value) => {
  if (!value) return EM_DASH;

  const [year, month, day] = value.split('-');
  const monthName = MONTH_NAMES[Number(month) - 1];
  if (!monthName) return EM_DASH;

  const dayNumber = Number(day);

  return `${monthName} ${dayNumber}${ordinalSuffix(dayNumber)}, ${year}`;
};

/**
 * `YYYY-MM-DD` as `MM/DD/YY`, for the list.
 *
 * The long form runs to ~40 characters for an application period, which
 * squeezed the cycle name out of its column.
 */
export const formatCycleDateShort = (value) => {
  if (!value) return EM_DASH;

  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return EM_DASH;

  return `${month}/${day}/${year.slice(-2)}`;
};

export const formatApplicationPeriodShort = (openDate, closeDate) =>
  `${formatCycleDateShort(openDate)} ${EN_DASH} ${formatCycleDateShort(closeDate)}`;

/**
 * `createdAt` as `MM/DD/YY`, in the reader's timezone.
 *
 * The one formatter here that builds a Date, because `createdAt` is an instant
 * rather than a calendar date — see the note at the top of this file.
 */
export const formatCreatedAtShort = (value) => {
  if (!value) return EM_DASH;

  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return EM_DASH;

  const month = String(instant.getMonth() + 1).padStart(2, '0');
  const day = String(instant.getDate()).padStart(2, '0');

  return `${month}/${day}/${String(instant.getFullYear()).slice(-2)}`;
};

export const mapCycle = (wire) => ({
  id: wire.id,
  name: wire.name,
  applicationOpenDate: wire.applicationOpenDate,
  applicationCloseDate: wire.applicationCloseDate,
  cycleEndDate: wire.cycleEndDate,
  createdAt: wire.createdAt,
  status: wire.status,
  statusLabel: CYCLE_STATUS_LABELS[wire.status] ?? wire.status,
  applicationPeriod: formatApplicationPeriodShort(
    wire.applicationOpenDate,
    wire.applicationCloseDate,
  ),
  endDateLabel: formatCycleDateShort(wire.cycleEndDate),
  // Shown in the list so its newest-first ordering is visible, not implied.
  createdAtLabel: formatCreatedAtShort(wire.createdAt),
  // Governs the roster-mutating controls only — Upload users and per-member
  // status edits — which the server rejects on a complete cycle. It does NOT
  // govern the Edit button: an *empty* complete cycle is still date-editable so
  // a mistyped end date can be rescued (GOTU-6393 / awards-service GOTU-5626),
  // a call CycleDetail makes from the unfiltered membership count this payload
  // doesn't carry.
  isRosterMutable: wire.status !== 'complete',
});

export const mapMembership = (wire) => {
  const profileType = USER_TYPE_TO_PROFILE[wire.userType];

  return {
    userId: wire.userId,
    name: wire.name,
    // The identities mirror is fed by Kafka and lags, so name can be empty.
    displayName: wire.name?.trim() ? wire.name : EM_DASH,
    userType: wire.userType,
    status: wire.status,
    statusLabel: MEMBERSHIP_STATUS_LABELS[wire.status] ?? wire.status,
    hasApplication: Boolean(wire.hasApplication),
    hasFeedback: Boolean(wire.hasFeedback),
    profilePath: profileType ? `/user/${profileType}/${wire.userId}` : null,
  };
};

const mapPage = (wire, listKey, mapItem) => ({
  items: (wire?.[listKey] ?? []).map(mapItem),
  page: wire?.page ?? 1,
  pageSize: wire?.pageSize ?? 15,
  totalCount: wire?.totalCount ?? 0,
});

export const mapCyclesPage = (wire) => mapPage(wire, 'cycles', mapCycle);

export const mapMembershipsPage = (wire) =>
  mapPage(wire, 'memberships', mapMembership);
